import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { db } from "../firebase";

/*
 * Collection
 *
 * applicantNotes
 */

const NOTES_COLLECTION = "applicantNotes";

export async function addApplicantNote({
  applicantId,
  tenantId,
  facilityId,
  note,
  createdBy,
  createdByName,
  attachment = null,
  attachments = [],
}) {
  const cleanApplicantId =
    String(applicantId || "").trim();

  const cleanTenantId =
    String(tenantId || "")
      .trim()
      .toLowerCase();

  const cleanFacilityId =
    String(facilityId || "")
      .trim()
      .toLowerCase();

  const cleanNote =
    String(note || "").trim();

  const cleanCreatedBy =
    String(createdBy || "").trim();

  if (!cleanApplicantId) {
    throw new Error(
      "Missing applicantId"
    );
  }

  if (!cleanTenantId) {
    throw new Error(
      "Missing tenantId"
    );
  }

  if (!cleanFacilityId) {
    throw new Error(
      "Missing facilityId"
    );
  }

  if (
    !cleanNote &&
    !attachment &&
    (!Array.isArray(attachments) ||
      attachments.length === 0)
  ) {
    throw new Error(
      "Applicant note or attachment is required"
    );
  }

  if (!cleanCreatedBy) {
    throw new Error(
      "Missing createdBy"
    );
  }

  const cleanAttachments =
    Array.isArray(attachments) &&
    attachments.length > 0
      ? attachments
      : attachment
        ? [attachment]
        : [];

  return addDoc(
    collection(
      db,
      NOTES_COLLECTION
    ),
    {
      applicantId:
        cleanApplicantId,

      /*
       * Canonical scope fields.
       */
      tenantId:
        cleanTenantId,

      facilityId:
        cleanFacilityId,

      note:
        cleanNote,

      attachment:
        attachment || null,

      attachments:
        cleanAttachments,

      createdBy:
        cleanCreatedBy,

      createdByName:
        createdByName ||
        "Administrator",

      pinned: false,
      archived: false,

      createdAt:
        serverTimestamp(),

      updatedAt:
        serverTimestamp(),
    }
  );
}

export function subscribeApplicantNotes(
  applicantId,
  callback,
  {
    tenantId = "",
    facilityId = "",
  } = {}
) {
  const cleanApplicantId =
    String(applicantId || "").trim();

  const cleanTenantId =
    String(tenantId || "")
      .trim()
      .toLowerCase();

  const cleanFacilityId =
    String(facilityId || "")
      .trim()
      .toLowerCase();

  if (!cleanApplicantId) {
    callback([]);
    return () => {};
  }

  const notesRef =
    collection(
      db,
      NOTES_COLLECTION
    );

  // Tenant scope is required even for tenant-wide administrators.
  if (!cleanTenantId || cleanTenantId === "all") {
    callback([]);
    return () => {};
  }
  const constraints = [where("applicantId", "==", cleanApplicantId), where("tenantId", "==", cleanTenantId)];
  if (cleanFacilityId && cleanFacilityId !== "all") constraints.push(where("facilityId", "==", cleanFacilityId));
  const notesQuery = query(notesRef, ...constraints);

  console.log(
    "APPLICANT NOTES LISTENER START",
    {
      applicantId:
        cleanApplicantId,

      tenantId:
        cleanTenantId ||
        "UNSCOPED",

      facilityId:
        cleanFacilityId ||
        "UNSCOPED",
    }
  );

  const unsubscribe =
    onSnapshot(
      notesQuery,

      (snapshot) => {
        const rows =
          snapshot.docs
            .map((noteDoc) => ({
              id: noteDoc.id,
              ...noteDoc.data(),
            }))
            .filter(
              (record) => {
                if (
                  !cleanTenantId ||
                  !cleanFacilityId
                ) {
                  return true;
                }

                return (
                  String(
                    record?.tenantId ||
                      ""
                  )
                    .trim()
                    .toLowerCase() ===
                    cleanTenantId &&

                  String(
                    record?.facilityId ||
                      ""
                  )
                    .trim()
                    .toLowerCase() ===
                    cleanFacilityId
                );
              }
            )
            .sort((a, b) => {
              /*
               * Pinned notes first.
               */
              if (
                Boolean(a.pinned) !==
                Boolean(b.pinned)
              ) {
                return a.pinned
                  ? -1
                  : 1;
              }

              /*
               * Newest first within
               * each pinned group.
               */
              const aTime =
                a.createdAt
                  ?.toMillis?.() ||
                (
                  a.createdAt
                    ?.seconds ||
                  0
                ) *
                  1000;

              const bTime =
                b.createdAt
                  ?.toMillis?.() ||
                (
                  b.createdAt
                    ?.seconds ||
                  0
                ) *
                  1000;

              return bTime - aTime;
            });

        console.log(
          "APPLICANT NOTES LISTENER SUCCESS",
          {
            applicantId:
              cleanApplicantId,
            tenantId:
              cleanTenantId ||
              "UNSCOPED",
            facilityId:
              cleanFacilityId ||
              "UNSCOPED",
            size: rows.length,
          }
        );

        callback(rows);
      },

      (error) => {
        console.error(
          "Applicant notes subscription failed:",
          {
            applicantId:
              cleanApplicantId,
            tenantId:
              cleanTenantId,
            facilityId:
              cleanFacilityId,
            code:
              error?.code,
            message:
              error?.message,
            error,
          }
        );

        callback([]);
      }
    );

  return () => {
    console.log(
      "APPLICANT NOTES LISTENER STOP",
      {
        applicantId:
          cleanApplicantId,
        tenantId:
          cleanTenantId,
        facilityId:
          cleanFacilityId,
      }
    );

    unsubscribe();
  };
}

export async function deleteApplicantNote(noteId) {
  if (!noteId) return;

  await deleteDoc(doc(db, NOTES_COLLECTION, noteId));
}

export async function updateApplicantNote({
  noteId,
  note,
  updatedBy,
  updatedByName,
}) {
  const text = String(note || "").trim();

  if (!noteId || !text) return;

  await updateDoc(
    doc(db, NOTES_COLLECTION, noteId),
    {
      note: text,
      updatedBy: updatedBy || null,
      updatedByName: updatedByName || null,
      updatedAt: serverTimestamp(),
      lastAction: "Edited",
    }
  );
}

export async function pinApplicantNote({
  noteId,
  updatedBy,
  updatedByName,
}) {
  if (!noteId) return;

  await updateDoc(
    doc(db, NOTES_COLLECTION, noteId),
    {
      pinned: true,
      updatedAt: serverTimestamp(),
      updatedBy: updatedBy || null,
      updatedByName: updatedByName || null,
      lastAction: "Pinned",
    }
  );
}

export async function unpinApplicantNote({
  noteId,
  updatedBy,
  updatedByName,
}) {
  if (!noteId) return;

  await updateDoc(
    doc(db, NOTES_COLLECTION, noteId),
    {
      pinned: false,
      updatedAt: serverTimestamp(),
      updatedBy: updatedBy || null,
      updatedByName: updatedByName || null,
      lastAction: "Unpinned",
    }
  );
}